import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ContentChild,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { Subject } from 'rxjs';

/**
 * Preset entity types. Each one drives the default 2-line option template:
 *   - product       → primary = longDescription, secondary = SKU · MRP · Pack · Brand
 *   - vendor        → primary = name,            secondary = City · GSTIN
 *   - manufacturer  → primary = name,            secondary = Code <code>
 *   - brand         → primary = name,            secondary = Code <code>
 *   - custom        → use [primaryField] / [secondaryFields] overrides
 *                     (or content-project a #optionTpl for full control)
 */
export type SearchSelectEntity =
  | 'product'
  | 'vendor'
  | 'manufacturer'
  | 'brand'
  | 'custom';

/**
 * Drop-in replacement for <ng-select> with a structured two-line option row,
 * standardised look + feel, and ControlValueAccessor support so [(ngModel)]
 * binding at call sites keeps working unchanged.
 *
 * Standard retail-ERP pattern: long names need a secondary disambiguator
 * (SKU / GSTIN / Code) so users can distinguish similar-named items.
 *
 * Migration from <ng-select> is mostly mechanical: swap the tag, add an
 * `entity` attribute. All forwarded inputs/outputs keep the same names.
 *
 * Escape hatches:
 *   - For non-preset shapes, supply [primaryField] / [secondaryFields].
 *   - For fully custom rows (e.g., PLU/EAN badges), project a template:
 *       <app-search-select ...>
 *         <ng-template #optionTpl let-item="item">...custom...</ng-template>
 *       </app-search-select>
 */
@Component({
  selector: 'app-search-select',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule],
  templateUrl: './search-select.component.html',
  styleUrls: ['./search-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchSelectComponent),
      multi: true,
    },
  ],
})
export class SearchSelectComponent implements ControlValueAccessor, AfterViewInit {
  /* -------------------- core ng-select passthroughs -------------------- */

  @Input() items: any[] = [];
  @Input() bindLabel: string = 'name';
  /** Omit to return the full object on change; set to e.g. 'id' for ID-only. */
  @Input() bindValue: string | undefined = undefined;
  @Input() placeholder: string = '';
  @Input() multiple: boolean = false;
  @Input() searchable: boolean = true;
  @Input() clearable: boolean = true;
  @Input() loading: boolean = false;
  @Input() virtualScroll: boolean = false;
  @Input() typeahead: Subject<string> | undefined = undefined;
  @Input() searchFn:
    | ((term: string, item: any) => boolean)
    | undefined = undefined;
  /** Direct [disabled] binding for non-Reactive usage. CVA's
   *  setDisabledState() also writes here, so FormControl.disable() works. */
  @Input() disabled: boolean = false;
  /** e.g. 'body': render the dropdown outside a clipping parent (dialogs). */
  @Input() appendTo: string | undefined = undefined;
  /** Put the cursor in the search box once rendered (e.g. a dialog's first field). */
  @Input() autoFocus: boolean = false;

  @ViewChild(NgSelectComponent) private ngSelect?: NgSelectComponent;

  /* -------------------- structured-row config -------------------- */

  /** Entity preset that drives the default option template. */
  @Input() entity: SearchSelectEntity = 'custom';
  /** Override which field name(s) drive the displayed primary text. */
  @Input() primaryField: string | undefined = undefined;
  /** Override which fields make up the secondary line. Supports dot paths
   *  (e.g. 'brand.name'). Used only when entity='custom' or to override
   *  a preset. */
  @Input() secondaryFields: string[] | undefined = undefined;

  /* -------------------- content-projection escape hatches -------------------- */

  /**
   * Custom dropdown-row template. Receives `{ item, searchTerm, index }`.
   *   <ng-template #optionTpl let-item="item">...</ng-template>
   */
  @ContentChild('optionTpl') optionTpl?: TemplateRef<any>;
  /**
   * Custom selected-label template (shown in the closed input).
   * Receives `{ item, clear }`.
   */
  @ContentChild('labelTpl') labelTpl?: TemplateRef<any>;

  /* -------------------- forwarded outputs -------------------- */

  @Output() change = new EventEmitter<any>();
  @Output() search = new EventEmitter<{ term?: string; items?: any[] }>();
  @Output() clear = new EventEmitter<void>();
  @Output() open = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();

  ngAfterViewInit(): void {
    // Deferred so it lands after the host (overlay/dialog) has painted.
    if (this.autoFocus) setTimeout(() => this.focus());
  }

  /** Open the list and put the cursor in its search box. The zf kit only
   *  displays the search input while open (_zf-ui.scss), so a plain focus()
   *  on a closed select would land on a hidden element and do nothing. */
  focus(): void {
    const select = this.ngSelect;
    if (!select || this.disabled) return;
    select.open();
    setTimeout(() => select.focus());
  }

  /* -------------------- ControlValueAccessor -------------------- */

  value: any = null;

  private onChangeFn: (v: any) => void = () => {};
  private onTouchedFn: () => void = () => {};

  writeValue(v: any): void {
    this.value = v;
  }
  registerOnChange(fn: (v: any) => void): void {
    this.onChangeFn = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouchedFn = fn;
  }
  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  onModelChange(v: any): void {
    this.value = v;
    this.onChangeFn(v);
    this.onTouchedFn();
  }

  /* -------------------- option row text helpers -------------------- */

  /** Primary line — what shows large/bold in the dropdown and in the
   *  closed input. Uses [primaryField] override, then [bindLabel]. */
  primaryText(item: any): string {
    if (!item) return '';
    const field = this.primaryField || this.bindLabel;
    return this.getField(item, field) || '-';
  }

  /** Secondary line — muted metadata below the primary. Honours
   *  [secondaryFields] override first, then falls back to entity preset. */
  secondaryText(item: any): string {
    if (!item) return '';
    if (this.secondaryFields && this.secondaryFields.length) {
      return this.secondaryFields
        .map((f) => this.getField(item, f))
        .filter((v) => v.length > 0)
        .join(' · ');
    }
    switch (this.entity) {
      case 'product': {
        const parts: string[] = [];
        const sku = item.skuCode ?? item.sku;
        if (sku) parts.push(`SKU ${sku}`);
        const mrp = item.mrp;
        if (mrp != null && mrp !== '') parts.push(`MRP ₹${mrp}`);
        const pack = item.innerPackaging ?? item.packSize;
        if (pack) parts.push(String(pack));
        const brand = item.brand?.name ?? item.brandName;
        if (brand) parts.push(String(brand));
        return parts.join(' · ');
      }
      case 'vendor': {
        return [item.city, item.gstin]
          .filter((v) => v && String(v).trim().length)
          .map(String)
          .join(' · ');
      }
      case 'manufacturer':
      case 'brand':
        return item.code ? `Code ${item.code}` : '';
      default:
        return '';
    }
  }

  /** Small mono meta shown next to the selected value in the closed box
   *  (vendor code, manufacturer / brand code, product SKU). */
  labelMeta(item: any): string {
    if (!item) return '';
    switch (this.entity) {
      case 'vendor':
        return item.vendorCode ? String(item.vendorCode) : '';
      case 'manufacturer':
      case 'brand':
        return item.code ? String(item.code) : '';
      case 'product': {
        const sku = item.skuCode ?? item.sku;
        return sku ? String(sku) : '';
      }
      default:
        return '';
    }
  }

  /** Resolve a dotted field path, e.g. 'brand.name' on item. */
  private getField(item: any, path: string): string {
    if (!item || !path) return '';
    const v = path.split('.').reduce((acc: any, k: string) => acc?.[k], item);
    return v !== undefined && v !== null && v !== '' ? String(v) : '';
  }
}
